


import {
  defineComponent,
  computed,
  reactive,
  onMounted,
  watch,
  PropType,
  onUnmounted,
  ref,
} from '@vue/composition-api';
import MyCalendarWithTime from '@/components/lib/MyCalendarWithTime.vue';
import PreviewFilesArea from '@/components/lib/PreviewFilesArea/index.vue';
import {
  dtFormat,
  getFuzzyDateTimeDifferenceString,
} from '@/lib/dateHelper';
import {
  MAX_SAVABLE_FILES_COUNT,
  FILE_TYPE_UNKNOWN,
  fetchFileAsObjectInfo,
  convertFilesToFileObjs,
  FileObj,
} from '@/lib/fileHelper';
import { convertFileObjsToCandidateFiles } from '@/lib/commentHelper';
import {
  NotificationInputModel,
  createInitialInputModel,
  nonNullableFields,
  toJaFieldMap,
} from '@/models/notification';
import { CandidateFile } from '@/models';
import { AdminGroup1 } from '@/models/apis/group1/adminGroup1Response';
import {
  NotificationCreateRequest,
  NotificationUpdateRequest,
} from '@/models/apis/notification/notificationRequest';
import { NotificationFile } from '@/models/apis/notification/notificationResponse';
import group1Api from '@/apis/group1';
import adminNotificationsApi from '@/apis/admin/admin_notifications';

export interface NotificationEditModalState {
  notification: NotificationInputModel;
  candidateFiles: CandidateFile[];
  fileEdit: {
    saveFiles: FileObj[];
    removeFileIds: number[];
  };
  groups1: AdminGroup1[];
  showEditClearConfirmModal: boolean;
  errors: Record<string, string>;
  isRequesting: boolean;
  infoMessage: string;
}

const NOTIFICATION_ACCEPTED_FILE_TYPES = 'image/*, application/pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx';
const NOTIFICATION_ATTACHMENT_FILE_MAX_MB = 10;
const NOTIFICATION_REFRESH_INTERVAL_MSEC = 60 * 1000;

export default defineComponent({
  components: {
    MyCalendarWithTime,
    PreviewFilesArea,
  },
  props: {
    notification: {
      type: Object as PropType<NotificationInputModel>,
      required: true,
    },
  },
  emits: ['close', 'success', 'show-error-message'],

  setup(props, { emit }) {
    const state = reactive<NotificationEditModalState>({
      notification: createInitialInputModel(),
      candidateFiles: [],
      fileEdit: {
        saveFiles: [],
        removeFileIds: [],
      },
      groups1: [],
      showEditClearConfirmModal: false,
      errors: {},
      isRequesting: false,
      infoMessage: '',
    });
    const intervalId = ref<ReturnType<typeof setInterval> | null>(null);
    const initialFileCount = ref<number>(0);

    const isNew = computed<boolean>(() =>
      !state.notification || !state.notification.id,
    );
    const modalTitle = computed<string>(() => {
      return isNew.value ? '新規作成' : '編集';
    });
    const isReadyToSave = computed<boolean>(() => {
      const hasTitle = !!state.notification.title.trim();
      const hasContent = !!state.notification.content.trim();
      const hasStartDate = !!state.notification.publishStartAt;
      const validFileCount = state.candidateFiles.length <= MAX_SAVABLE_FILES_COUNT;

      return hasTitle && hasContent && hasStartDate && validFileCount && !state.isRequesting;
    });

    const loadGroups1 = async(): Promise<void> => {
      try {
        const response = await group1Api.adminIndex();
        state.groups1 = response.data;
        if (state.groups1.length === 1) {
          state.notification.group1Id = state.groups1[0].id;
        }
      } catch (error) {
        emit('show-error-message', '組織情報の読み込みに失敗しました');
      }
    };
    const validateForm = (notification: NotificationInputModel): boolean => {
      state.errors = {};

      for (const key in notification) {
        if (nonNullableFields.includes(key)) {
          const value = notification[key as keyof NotificationInputModel];
          if (!value) {
            const title = toJaFieldMap.find((item) => item.key === key)?.value ?? '';
            state.errors[key] = `${title}は必須です`;
          }
        }
      }

      if (notification.publishEndAt) {
        const startDate = new Date(notification.publishStartAt);
        const endDate = new Date(notification.publishEndAt);
        if (endDate <= startDate) {
          state.errors.publishEndAt = '掲載開始日時 < 掲載終了日時 となるよう入力してください';
        } else if (endDate <= new Date()) {
          state.errors.publishEndAt = '現在時刻 < 掲載終了日時 となるよう入力してください';
        }
      }
      return Object.keys(state.errors).length === 0;
    };
    const saveNotification = async(): Promise<void> => {
      if (!validateForm(state.notification)) return;

      state.isRequesting = true;
      const saveFiles = state.fileEdit.saveFiles.map(e => e.file);

      try {
        const notificationRequestParameters = {
          title: state.notification.title.trim(),
          content: state.notification.content.trim(),
          publish_start_at: state.notification.publishStartAt,
          publish_end_at: state.notification.publishEndAt || '',
          group1_id: state.notification.group1Id || null,
        };

        if (isNew.value) {
          const createData: NotificationCreateRequest = {
            ...notificationRequestParameters,
            save_files: saveFiles,
          };
          await adminNotificationsApi.create(createData);
        } else {
          const updateData: NotificationUpdateRequest = {
            ...notificationRequestParameters,
            save_files: saveFiles,
            remove_file_ids: state.fileEdit.removeFileIds,
          };
          await adminNotificationsApi.update(state.notification.id, updateData);
        }

        emit('success');
        closeEditModal();
      } catch (e) {
        const errorMessage = (e as any)?.response?.data?.message || '保存に失敗しました。再度操作を行ってください';
        emit('show-error-message', errorMessage);
      } finally {
        state.isRequesting = false;
      }
    };
    const tryCloseEditModal = (): void => {
      if (isEdited()) {
        state.showEditClearConfirmModal = true;
        return;
      }

      closeEditModal();
    };
    const closeEditModal = (): void => {
      state.notification = createInitialInputModel();
      state.candidateFiles = [];
      state.fileEdit = {
        saveFiles: [],
        removeFileIds: [],
      };
      state.showEditClearConfirmModal = false;
      state.errors = {};
      emit('close');
    };
    const handleFileUpload = async(fileList: FileList): Promise<void> => {
      delete state.errors['uploadFiles'];

      if (!fileList || fileList.length === 0) return;

      const uploadFilesCount = fileList.length + state.candidateFiles.length;
      try {
        if (uploadFilesCount > MAX_SAVABLE_FILES_COUNT) {
          const errorMsg = `添付ファイルの最大数は${MAX_SAVABLE_FILES_COUNT}個です。`;
          state.errors['uploadFiles'] = errorMsg;
          return;
        }

        const maxId = state.candidateFiles.length > 0
          ? state.candidateFiles.map(e => e.id).reduce((a, b) => Math.max(a, b), 0)
          : 0;
        const attachedFileObjs = await convertFilesToFileObjs(fileList, maxId, NOTIFICATION_ATTACHMENT_FILE_MAX_MB);
        state.candidateFiles.push(...convertFileObjsToCandidateFiles(attachedFileObjs));
        state.fileEdit.saveFiles.push(...attachedFileObjs);
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'ファイルアップロードエラー';
        state.errors['uploadFiles'] = errorMessage;
      }
    };
    const removeFile = (fileId: number): void => {
      state.candidateFiles = state.candidateFiles.filter(e => e.id !== fileId);

      const saveFileIndex = state.fileEdit.saveFiles.findIndex((e: FileObj) => e.id === fileId);
      if (saveFileIndex !== -1) {
        state.fileEdit.saveFiles.splice(saveFileIndex, 1);
        return;
      }
      state.fileEdit.removeFileIds.push(fileId);
    };
    const isEdited = (): boolean => {
      if (!props.notification) return false;

      const initial = props.notification;
      const current = state.notification;
      if (current.title.trim() !== initial.title.trim()) return true;
      if (current.content.trim() !== initial.content.trim()) return true;
      if (current.publishStartAt !== initial.publishStartAt) return true;
      if (current.publishEndAt !== initial.publishEndAt) return true;
      if (current.group1Id !== initial.group1Id) return true;

      if (state.candidateFiles.length !== initialFileCount.value) return true;
      if (state.fileEdit.removeFileIds.length > 0) return true;

      return false;
    };
    const onStartDateTimeInput = (dateValue: Date): void => {
      state.notification.publishStartAt = dtFormat(dateValue, 'yyyy-mm-dd HH:MM');
    };
    const onEndDateTimeInput = (dateValue: Date): void => {
      state.notification.publishEndAt = dtFormat(dateValue, 'yyyy-mm-dd HH:MM');
    };
    const updateInfoMessage = (): void => {
      const now = new Date();
      const publishStr = state.notification.publishStartAt;
      const publishTime = new Date(publishStr);
      if (publishTime > now) {
        const fuzzyDateTimeDiffStr = getFuzzyDateTimeDifferenceString(publishTime, now);
        state.infoMessage = `${fuzzyDateTimeDiffStr}に掲載を開始します。`;
        return;
      }
      state.infoMessage = '保存後すぐに掲載を開始します。';
    };
    const getSavedNotificationFile = async(notificationFile: NotificationFile): Promise<CandidateFile | null> => {
      const savedFileInfo = await fetchFileAsObjectInfo(notificationFile.file_path);
      if (!savedFileInfo?.url) return null;

      return {
        id: notificationFile.id,
        content: savedFileInfo.url,
        type: savedFileInfo.type ?? FILE_TYPE_UNKNOWN,
        name: notificationFile.file_name,
        path: notificationFile.file_path,
        selected: false,
      };
    };

    watch(
      () => state.notification.publishStartAt,
      () => {
        updateInfoMessage();
      },
      { immediate: true },
    );
    onMounted(async() => {
      state.notification = props.notification ? { ...props.notification } : createInitialInputModel();
      intervalId.value = setInterval(updateInfoMessage, NOTIFICATION_REFRESH_INTERVAL_MSEC);
      await loadGroups1();
      const notificationFiles = state.notification.saveFiles ?? [];
      const savedFiles = await Promise.all(
        notificationFiles.map((notificationFile) => getSavedNotificationFile(notificationFile)),
      );
      const validFiles = savedFiles.filter((e): e is CandidateFile => e !== null);
      state.candidateFiles.push(...validFiles);
      initialFileCount.value = state.candidateFiles.length;
    });
    onUnmounted((): void => {
      if (intervalId.value) {
        clearInterval(intervalId.value);
        intervalId.value = null;
      }
    });

    return {
      state,
      isReadyToSave,
      modalTitle,
      tryCloseEditModal,
      closeEditModal,
      saveNotification,
      handleFileUpload,
      removeFile,
      onStartDateTimeInput,
      onEndDateTimeInput,
      NOTIFICATION_ACCEPTED_FILE_TYPES,
    };
  },
});
